import { PayloadAction } from '@reduxjs/toolkit';

import { ContactPoint, PaginationUser } from '@/entities/user';
import { AppLanguage } from '@/shared/constants/app-language.constant';
import { AppStorage } from '@/shared/constants/app-storage.constant';
import { AppTheme } from '@/shared/constants/app-theme.constant';
import { createSlice } from '@/shared/lib/helpers/create-store-logic';
import { validateField } from '@/shared/lib/helpers/validation-error.helper';

import { User, UserSchema } from '../types/user.interface';
import { changeProfileFormSchema } from '../validation/change-profile-form-schema.schema';

const initialState: UserSchema = {
	inited: typeof window !== 'undefined' ? !localStorage.getItem(AppStorage.Token) : false,
	isChangeEmail: false,
	validationError: {},
};

export const userInitialState: User = {
	id: '',
	username: '',
	email: '',
	permissions: [],
	domains: [],
	theme: AppTheme.Dark,
	language: AppLanguage.English,
	allowDashboardEdit: false,
	contactPoints: [],
	telegram: '',
	isPlant: false,
	plantAddress: '',
	acceptedLatestAgreements: false,
	acceptedTimeTC: '',
	acceptedTimePP: '',
	dataSharing: false,
	automatedPlanAccess: false,
	accountType: undefined,
	scopedRole: undefined,
	realmRoles: [],
	latestAcceptedTermsAndConditionsUrl: '',
	latestPrivacyPolicyUrl: '',
	isAcceptedLatestTermsAndConditions: false,
	isAcceptedLatestPrivacyPolicy: false,
	isProxyLogin: false,
};

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		setUser: (state, action: PayloadAction<User>) => {
			state.data = action.payload;
		},
		updateUser: (state, action: PayloadAction<PaginationUser>) => {
			state.data = {
				...state.data,
				...action.payload,
				email: action.payload.email ?? state.data?.email ?? userInitialState.email,
				theme: action.payload.theme ?? state.data?.theme ?? userInitialState.theme,
				language: action.payload.language ?? state.data?.language ?? userInitialState.language,
				allowDashboardEdit: action.payload.editDashboard ?? state.data?.allowDashboardEdit,
				permissions: state.data?.permissions ?? userInitialState.permissions,
				domains: state.data?.domains ?? userInitialState.domains,
				contactPoints: state.data?.contactPoints ?? userInitialState.contactPoints,
				telegram: state.data?.telegram ?? userInitialState.telegram,
				isPlant: state.data?.isPlant ?? userInitialState.isPlant,
				plantAddress: state.data?.plantAddress ?? userInitialState.plantAddress,
				acceptedLatestAgreements: state.data?.acceptedLatestAgreements ?? userInitialState.acceptedLatestAgreements,
				acceptedTimeTC: state.data?.acceptedTimeTC ?? userInitialState.acceptedTimeTC,
				acceptedTimePP: state.data?.acceptedTimePP ?? userInitialState.acceptedTimePP,
				dataSharing: action.payload.dataSharing ?? state.data?.dataSharing ?? userInitialState.dataSharing,
				automatedPlanAccess: state.data?.automatedPlanAccess ?? userInitialState.automatedPlanAccess,
				accountType: action.payload.accountType ?? state.data?.accountType,
				scopedRole: action.payload.scopedRole ?? state.data?.scopedRole,
				realmRoles: state.data?.realmRoles ?? userInitialState.realmRoles,
				latestAcceptedTermsAndConditionsUrl:
					state.data?.latestAcceptedTermsAndConditionsUrl ?? userInitialState.latestAcceptedTermsAndConditionsUrl,
				latestPrivacyPolicyUrl: state.data?.latestPrivacyPolicyUrl ?? userInitialState.latestPrivacyPolicyUrl,
				isAcceptedLatestTermsAndConditions:
					state.data?.isAcceptedLatestTermsAndConditions ?? userInitialState.isAcceptedLatestTermsAndConditions,
				isAcceptedLatestPrivacyPolicy:
					state.data?.isAcceptedLatestPrivacyPolicy ?? userInitialState.isAcceptedLatestPrivacyPolicy,
				isProxyLogin: state.data?.isProxyLogin ?? userInitialState.isProxyLogin,
			};
		},
		setAccessToken: (state, action: PayloadAction<string>) => {
			state.accessToken = action.payload;
		},
		startSession: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
			localStorage.setItem(AppStorage.AccessToken, action.payload.accessToken);
			localStorage.setItem(AppStorage.Token, action.payload.refreshToken);
			// Makes UserGate load the user instead of treating the session as unauthenticated
			state.inited = false;
		},
		setInited: (state, action: PayloadAction<boolean>) => {
			state.inited = action.payload;
		},
		changeTheme: (state, action: PayloadAction<AppTheme>) => {
			if (state?.data?.theme) {
				state.data.theme = action.payload;
			}
		},
		changeLanguage: (state, action: PayloadAction<AppLanguage>) => {
			if (state?.data?.language) {
				state.data.language = action.payload;
			}
		},
		setContactPoints: (state, action: PayloadAction<ContactPoint[]>) => {
			if (state.data) {
				state.data.contactPoints = action.payload;
			}
		},
		setTelegram: (state, action: PayloadAction<string>) => {
			if (state.data) {
				state.data.telegram = action.payload;
			}
		},
		setEmail: (state, action: PayloadAction<string>) => {
			if (state.data) {
				state.data.email = action.payload;
			}
		},
		setDataSharing: (state, action: PayloadAction<boolean>) => {
			if (state.data) {
				state.data.dataSharing = action.payload;
			}
		},
		logout: (state) => {
			state.data = undefined;
			state.accessToken = undefined;
			Object.values(AppStorage).forEach((storageKey) => {
				localStorage.removeItem(storageKey);
			});
		},
		setIsChangeEmail: (state, action: PayloadAction<boolean>) => {
			state.isChangeEmail = action.payload;
		},
		setValidationError: (state) => {
			if (state.data) {
				state.validationError.email = validateField(changeProfileFormSchema, 'email', { email: state.data.email });
			}
		},
	},
});

export const { actions: userActions, reducer: userReducer, useActions: useUserActions } = userSlice;
